import { TResult, makeFailure, makeSuccess } from "../../sg-core/utils/Result";
import { TTokenAuthResponse } from "../models/GateAuthResponse";
import UserMapper from "../mappers/UserMapper";
import { IUser } from "../models/User";
import { ISgApiService } from "../../sg-core";

export interface ILoginResult {
    user: IUser;
    token: string;
    returnHref: string;
    apiLanguage: string;
    applicationGuid: string;
}

export interface ISgAuthService {
    activateReturnToken: (returnToken: string) => Promise<TResult<ILoginResult>>;
    activateToken: (token: string, isReturn?: boolean) => Promise<TResult<ILoginResult>>;
    activateTokenViaParam: () => Promise<TResult<ILoginResult>>;
    fetchSessionDetail: (sid: string) => Promise<TResult<ILoginResult>>;
    endSession: (token: string) => Promise<TResult<boolean>>;
    addTokenToServices: (token: string) => void;
    changeSessionLanguage(lang: "en" | "us" | "fi", token: string): Promise<TResult<boolean>>;
};

export const getAuthService = (endpoint: string, anonymousSession: boolean, apiServices: ISgApiService[] = []): ISgAuthService => {

    const addTokenToServices = (token: string) => {
        apiServices.forEach((apiService) => { apiService.setToken(token); })
    }

    const activateReturnToken: ISgAuthService["activateReturnToken"] = async (returnToken) => {
        return await activateToken(returnToken, true);
    }

    const activateToken: ISgAuthService["activateToken"] = async (token, isReturn = false) => {
        const body: any = {
            action: "autentikoi_gate_token",
            token: token
        }

        if (isReturn) {
            body.token = undefined;
            body.palautustoken = token;
        }

        if (!body.token && !body.palautustoken) {
            return makeFailure(new Error("Token not set"));
        }

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                body: JSON.stringify(body),
            });

            if (response.ok) {
                const gateResponse: TTokenAuthResponse = await response.json();
                if (gateResponse.success === true) {
                    const user = UserMapper.apiToDomain(gateResponse);

                    addTokenToServices(`${gateResponse.sid}`);

                    return makeSuccess({
                        user,
                        token: `${gateResponse.sid}`,
                        returnHref: gateResponse.paluuosoite,
                        apiLanguage: (gateResponse.henkilo.api || gateResponse.henkilo.api2).kieli,
                        applicationGuid: gateResponse["sovellus-guid"],
                    });

                } else {
                    const error = new Error(gateResponse.error.message);
                    return makeFailure(error as Error);
                }
            } else {
                return makeFailure(new Error(response.statusText));
            }
        } catch (e) {
            console.error(e);
            return makeFailure(e as Error);
        }
    };



    const activateTokenViaParam: ISgAuthService["activateTokenViaParam"] = async () => {
        const { search } = window.location;
        const regex = /^\?.*token=([A-Za-z0-9-]+)&?$/g;

        const regexResult = regex.exec(search);

        if (regexResult === null) {
            return makeFailure(Error("No token in URL"))
        }

        const [_1, token] = regexResult; // eslint-disable-line

        // Remove token form url
        window.history.pushState("object or string", "Title", window.location.pathname);
        return await activateToken(token);
    };

    const fetchSessionDetail: ISgAuthService["fetchSessionDetail"] = async (sid) => {
        const response = await fetch(endpoint, {
            method: "POST",
            headers: { Authorization: "NAS SID " + sid },
            body: JSON.stringify({ action: "hae_istunnon_tiedot" }),
        });

        if (response.ok) {
            const gateResponse: TTokenAuthResponse = await response.json();
            if (gateResponse.success === true) {
                const user = UserMapper.apiToDomain(gateResponse);
                addTokenToServices(`${sid}`);
                return makeSuccess({
                    user,
                    token: `${sid}`,
                    returnHref: gateResponse.paluuosoite,
                    apiLanguage: gateResponse.henkilo?.api?.kieli,
                    applicationGuid: gateResponse["sovellus-guid"],
                });

            } else {
                const error = new Error(gateResponse.error.message);
                return makeFailure(error as Error);
            }
        } else {
            return makeFailure(new Error(response.statusText));
        }
    };

    const endSession: ISgAuthService["endSession"] = async (token) => {

        const response = await fetch(endpoint, {
            method: "POST",
            headers: { Authorization: "NAS SID " + token },
            body: JSON.stringify({ action: anonymousSession ? "katkaise_istunto_anonyymi" : "katkaise_istunto" }),
        });

        if (!response.ok) {
            return makeFailure(new Error(response.statusText));
        };

        const result: TTokenAuthResponse = await response.json();
        if (result.success !== true) {
            return makeFailure(new Error(result.error.message));
        };

        return makeSuccess(true);
    };

    const changeSessionLanguage: ISgAuthService["changeSessionLanguage"] = async (lang, token) => {
        const body = {
            action: anonymousSession ? "vaihda_kieli_anonyymi" : "vaihda_kieli",
            kieli: ["en", "us"].includes(lang) ? "us" : lang
        };

        const response = await fetch(endpoint, {
            method: "POST",
            headers: { Authorization: "NAS SID " + token },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            return makeFailure(new Error(response.statusText));
        };

        const result: any = await response.json();

        if (result.success !== true) {
            return makeFailure(new Error(result.error.message));
        };

        return makeSuccess(true);
    }

    return {
        changeSessionLanguage,
        activateToken,
        activateTokenViaParam,
        activateReturnToken,
        endSession,
        fetchSessionDetail,
        addTokenToServices
    };
};
